import { AI_PRIORITIES } from '../schemas';
import type { QuickTaskDraftFormValues } from '../schemas';
import type { ProposalField, QuickTaskProposal } from '../types';

/**
 * What the draft starts holding for a status or a group.
 *
 * Blank when the proposal offered a choice, so the user has to make it — but *not* when the
 * value is one the board does not have yet. That case holds the name, which is what tells the
 * apply payload the user is still confirming the entity being created; blanking it there meant
 * a proposed new column was announced on screen, warned about, and then quietly not created.
 *
 * Not used for the assignee: a person is never created here, so `isNew` has no meaning on that
 * field, and honouring it would hold a value where the candidate list exists to force a choice.
 */
function draftValueFor(field: ProposalField): string {
  if (field.isNew) return field.value ?? '';
  return pickedOrBlank(field);
}

/** Blank while a choice is outstanding, the value otherwise. */
function pickedOrBlank(field: ProposalField): string {
  return (field.candidates?.length ?? 0) > 1 ? '' : (field.value ?? '');
}

/** Builds form values from a proposal. Selects use '' as the Radix empty sentinel. */
export function proposalToFormValues(proposal: QuickTaskProposal): QuickTaskDraftFormValues {
  const { fields } = proposal;
  const priorityValue = fields.priority.value;
  const validPriority: QuickTaskDraftFormValues['priority'] = AI_PRIORITIES.includes(
    priorityValue as (typeof AI_PRIORITIES)[number],
  )
    ? (priorityValue as (typeof AI_PRIORITIES)[number])
    : null;

  return {
    title: fields.title.value ?? '',
    description: fields.description.value ?? '',
    startDate: fields.startDate.value ?? null,
    dueDate: fields.dueDate.value ?? null,
    statusId: draftValueFor(fields.statusId),
    assigneeId: pickedOrBlank(fields.assigneeId),
    priority: validPriority,
    // The proposal carries every value as a string; only this one is a number in the form.
    // `!= null` rather than truthiness. No input of the declared `string | null` type behaves
    // differently today — `'0'` is truthy — but the guard should say what it means, because the
    // schema allows 0 and truthiness would drop it the moment the value stops being a string.
    estimatedMinutes:
      fields.estimatedMinutes?.value != null && fields.estimatedMinutes.value !== ''
        ? Number(fields.estimatedMinutes.value)
        : null,
    groupId: fields.groupId ? draftValueFor(fields.groupId) : '',
    // Every proposed tag, by id when the project has it and by name when it is still to be
    // created — the same identifiers `toApplyRequest` reads back.
    tags: proposal.tags.map((tag) => tag.id ?? tag.name),
  };
}
